import { useHistory } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import { usePlanStatus } from "@/react/hooks/usePlanStatus";
import type { Plan } from "@circle-react/types";
import { useUpgradePlanModal } from "@circle-react-shared/UpgradePlan";
import { usePlans } from "../PlansContext";

export interface PlanActionHandlers {
  handleChangePlanButtonClick: () => Promise<void>;
  handleContactSupportOnClick: () => void;
  handleCurrentPlanButtonClick: () => void;
  handleRedeemOfferButtonClick: () => Promise<void>;
  handleRenewButtonClick: () => Promise<unknown>;
  handleResumeButtonClick: () => void;
}

export const usePlanActions = (plan: Plan): PlanActionHandlers => {
  const { updateSubscription, refetchPlans, refetchCurrentPlan } = usePlans();

  const history = useHistory();
  const upgradePlanModal = useUpgradePlanModal();
  const { shouldShowProration, isCommunityOnTrial } = usePlanStatus();

  const getPlanId = () => plan.professional_grandfathered_id || plan.id;

  const onCloseModal = () => {
    refetchPlans();
    refetchCurrentPlan();
  };

  const onChangePlanSuccess = async () => {
    history.push("/feed");
    await upgradePlanModal.hide();
  };

  const getModalConfig = () => {
    const shouldShowPlanDetails = isCommunityOnTrial
      ? false
      : !shouldShowProration(plan);

    return {
      planId: getPlanId(),
      onSuccess: onChangePlanSuccess,
      successButtonLabel: t("pricing_plans.change_plan.go_to_community"),
      showPlanDetails: shouldShowPlanDetails,
      onCloseModal: onCloseModal,
    };
  };

  const showUpgradeModal = async () => {
    await upgradePlanModal.show(getModalConfig());
  };

  const handleChangePlanButtonClick = async () => {
    await showUpgradeModal();
  };

  const handleRedeemOfferButtonClick = async () => {
    await showUpgradeModal();
  };

  const handleResumeButtonClick = () => {
    updateSubscription(getPlanId());
  };

  const handleRenewButtonClick = () =>
    upgradePlanModal.show({
      planId: getPlanId(),
      onSuccess: () => onChangePlanSuccess(),
      successButtonLabel: t("pricing_plans.change_plan.go_to_community"),
      onCloseModal: onCloseModal,
      isRenewal: true,
    });

  // Do nothing on current plan button click
  const handleCurrentPlanButtonClick = () => {};

  const handleContactSupportOnClick = () => {
    window.open("mailto:support@circle.so", "emailWindow");
  };

  return {
    handleChangePlanButtonClick,
    handleRedeemOfferButtonClick,
    handleResumeButtonClick,
    handleRenewButtonClick,
    handleCurrentPlanButtonClick,
    handleContactSupportOnClick,
  };
};
